<template>
    <!-- A face, shown as a face.

         This was 53 near-identical thumbnails in a flat grid running off the
         bottom of the window: no hierarchy, no way to tell at a glance which
         one was yours, and a page that scrolled away from its own controls.
         It is now the shape the lobby already uses for exactly this job —
         the chosen thing large, and everything else a reel along the foot. -->
    <div class="pl-portrait">
        <div class="pl-portrait__stage">
            <figure class="pl-portrait__hero">
                <img
                    class="pl-portrait__plate"
                    :src="mediaUrl(chosenTile.image)"
                    :alt="labelOf(chosenTile)"
                    decoding="async"
                />
                <figcaption class="pl-portrait__caption">
                    <span class="pl-portrait__mark">{{ t('player.creation.portrait.chosen') }}</span>
                </figcaption>
            </figure>

            <h1 ref="heading" tabindex="-1" class="pl-visually-hidden">
                {{ t('player.creation.portrait.title') }}
            </h1>
            <p class="pl-portrait__hint">{{ t('player.creation.portrait.hint') }}</p>
        </div>

        <!-- The lobby's own filmstrip, on its third consumer: a ring with no
             ends, no scrollbar and an arrow at each side. A scroll container
             here meant a permanent bar across a near-black screen and a strip
             a player could reach the end of; the ring has neither. -->
        <LobbyStrip
            class="pl-strip--faces"
            :items="stripItems"
            :selected-id="selectedKey"
            :open-at-id="selectedKey"
            :label="labelOf"
            :aria-label="t('player.creation.portrait.title')"
            @select="onSelect"
        >
            <template #default="{ item }">
                <img
                    class="pl-mini__art pl-mini__art--face"
                    :src="mediaUrl(item.image)"
                    :alt="labelOf(item)"
                    decoding="async"
                />
            </template>
        </LobbyStrip>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LobbyStrip from '@/components/Player/LobbyStrip.vue';
import { mediaUrl } from '@/components/Player/mediaUrl.js';
import { DEFAULT_PRESET_ID, presetTiles } from '@/utils/avatarPresets.js';

/* The strip keys and selects on `item.id`, and the placeholder tile's id is
   `null` — which is a real value here, meaning "whatever the server would
   assign". It gets a stand-in key inside the strip and is mapped back on the
   way out, so nothing outside this component learns about it. */
const DEFAULT_KEY = '__unmarked__';

const props = defineProps({
    eyebrow: { type: String, default: '' },
    presets: { type: Array, default: () => [] },
    path: { type: Object, default: null },
    gender: { type: String, default: '' },
    modelValue: { type: String, default: null },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const heading = ref(null);

const tiles = computed(() => presetTiles(props.presets, props.path, props.gender));

/* The label is carried on the item rather than derived on demand, because it
   is positional and neither the strip nor the hero knows an index. */
const stripItems = computed(() =>
    tiles.value.map((tile, index) => ({
        ...tile,
        id: tile.id ?? DEFAULT_KEY,
        label: index === 0
            ? t('player.creation.portrait.defaultName')
            : t('player.creation.portrait.option', { n: index }),
    })),
);

const selectedKey = computed(() => props.modelValue ?? DEFAULT_KEY);

function onSelect(key) {
    select(key === DEFAULT_KEY ? DEFAULT_PRESET_ID : key);
}

/* The hero always has something to show: the first tile is the placeholder the
   server would assign with nothing chosen, so "nothing selected" is still a
   face rather than an empty frame. */
const chosenTile = computed(
    () => stripItems.value.find((tile) => tile.id === selectedKey.value) ?? stripItems.value[0],
);

function select(id) {
    emit('update:modelValue', id);
}

/* Screen readers only — nothing here is drawn. The rows carry the name of the
   school their art was commissioned for ("Constructor School"), which is
   authoring metadata about the picture rather than anything the player is
   choosing, and putting it on the plate said the wrong thing about what this
   step is. A face is picked by looking at it; its position is all a
   non-visual reading needs to tell one from the next. */
function labelOf(tile) {
    return tile?.label ?? t('player.creation.portrait.defaultName');
}

onMounted(async () => {
    await nextTick();
    heading.value?.focus();
});
</script>
